import type { UiStrings } from '../types'

const en: UiStrings = {
  brand: 'Holecard EX',
  docTitleSuffix: 'Process record',
  tagline: 'Record seat · holes · board · line — shareable process card',
  metaDescription: 'Holecard EX — mobile-friendly poker process record. Log seat, hole cards, board, and street actions into a shareable card.',
  stepsAria: 'Steps',
  prev: 'Back',
  undo: 'Undo',
  next: 'Next',
  nextSummary: 'View summary',
  privacyLink: 'Privacy & ads policy',
  privacyTitle: 'Privacy & ads policy',
  privacyHint:
    'Draft for AdSense review. Please review and update before production use.',
  privacyBack: 'Back to app',
  privacyCollectTitle: '1. Information we collect',
  privacyCollectBody:
    'Holecard EX currently assembles hand-review drafts in your browser only. No account login is required, and hand data is not sent to a server. Drafts may be kept temporarily in this tab sessionStorage (this policy will be updated if that changes).',
  privacyAdsTitle: '2. Cookies & ads',
  privacyAdsBody:
    'Third-party ad networks such as Google AdSense may be used. Advertisers may use cookies or device identifiers for interest-based ads. You can control personalized ads in your browser or Google Ads settings.',
  privacyContactTitle: '3. Contact',
  privacyContactBody:
    'For policy questions, open a GitHub issue for holdcard-ex.com. Operator email: see TODO_OPERATOR_EMAIL in project config.',
  langAria: 'Language',
  langCompactAria: 'Language (compact)',
  koPathLink: '한국어',
  koPathLinkAria: 'Korean version (/ko)',
  enPathLink: 'English',
  enPathLinkAria: 'English version (/)',

  setupTitle: 'Table',
  setupHint: 'Players · stack · blinds — then Seat → Hand → Line',
  players: 'Players',
  playersUnit: (n) => `${n}-max`,
  sb: 'SB',
  bb: 'BB',
  ante: 'Ante (optional)',
  anteHint: 'Same unit as blinds',
  effectiveStack: 'Effective stack (BB, optional)',
  potBb: 'Pot (BB, optional)',
  metaOptionalHint: 'Leave ante / eff stack / pot empty to hide them in summaries',
  setupDetailsToggle: 'Players · stack · ante · eff · pot · chips',
  setupBasicsHint: 'Defaults fine (6-max · 100bb) — Next or skip ahead',
  setupMetaSetChip: 'Stack · ante · eff · pot set',
  stackUnit: 'Stack unit',
  stackUnitBb: 'BB',
  stackUnitChips: 'Chips',
  stackChips: 'Stack (chips)',
  stackBb: 'Stack (BB)',
  stackChipsUnit: 'chips',

  positionTitle: 'Seat',
  seatQuestion: 'Where did you sit?',
  settingsBack: 'Back',
  settingsSegAria: 'Table settings (players · blinds · stack)',
  lineSeatEditAria: 'Edit seat',
  lineHoleEditAria: 'Edit hole cards',
  positionHint: (count, hint) =>
    `${count}-max · my seat${hint ? ` · ${hint}` : ''}`,

  tableVizAria: (n) => `${n}-max poker table seats`,
  tableVizHeroBadge: 'you',
  tableVizHeroSeatAria: (seat) => `${seat} (you)`,
  tableVizDealer: 'D',
  tableVizDealerTitle: 'Dealer button',
  tableVizTapHint: 'Tap your seat on the table',
  tableVizListFallback: 'List seats · codes',
  tableVizBlindsAria: (v) => `Blinds ${v}`,
  tableVizPotPlaceholder: 'Pot',
  tableVizPotValue: (v) => `Pot ${v}`,
  tableVizPotAria: (v) => `Pot ${v}`,
  tableVizStackAria: (v) => `Stack ${v}`,
  tableVizEmptyMark: '—',
  tableVizEffHint: (v) => `Eff ${v}bb`,
  tableVizEffTitle: 'Effective stack (table-wide, not per seat)',
  tableVizStepDown: 'Decrease',
  tableVizStepUp: 'Increase',
  tableVizEditDone: 'Done',
  tableVizInfoHint: 'Tap blinds, pot, or stack on the table to edit',
  seatAbbrevHint: 'Seat abbreviations. Tap for the full name.',

  cardsTitle: 'Hand',
  cardsHint:
    'My hole cards first · optional opponents via “Add opponents”',
  heroHolesGuide: (seat) => `Pick the two ${seat} hole cards.`,
  cardSlot1: 'Card 1',
  cardSlot2: 'Card 2',
  cardSlotSelect: 'Select',
  seatsLabel: 'Seats',
  knownHolesOptional: 'Opponent hole cards are optional (showdown / review — leave empty OK)',
  holesBySeat: 'Hole cards · pick a seat',
  seatVillain: (pos) => `Villain ${pos}`,
  seatFilled: 'Set',
  holesModeHeroOnly: 'My cards only',
  holesModeWithVillains: 'Add opponents',
  holesModeAria: 'Hole-card entry mode',
  villainNudgeToast: 'Know a villain hand? Tap BB / CO etc.',
  heroCardsDoneCta: 'Go to line',
  heroCardsAddVillainsCta: 'Add opponents',
  suitPickerLabel: 'Suit',
  rankPickerLabel: 'Rank',

  boardTitle: 'Board',
  boardHint: 'When you get there — skip for preflop-only',
  flop: 'Flop',
  turn: 'Turn',
  river: 'River',
  flopSlot: (i) => `F${i}`,

  actionsTitle: 'Line',
  actionsHint: 'Street-by-street action line — fold / check / call / bet / raise / all-in',
  actorQuick: 'Quick actor',
  actor: 'Actor',
  actorPlaceholder: 'Enter name',
  actorCustomToggle: 'Custom name',
  actorRequired: 'Enter or select an actor',
  action: 'Action',
  sizeLabel: 'Size',
  sizePlaceholder: 'e.g. 2.5bb',
  sizeRequired: 'Enter a size for bet/raise',
  sizeCustomToggle: 'Custom',
  sizeOpenApprox: 'Open',
  sizePotApprox: 'Pot size',
  sizeOverbetApprox: 'Overbet',
  sizeJamApprox: 'Short / jam',
  sizeUnitBb: 'bb',
  sizeUnitPct: 'pot',
  snapHint: 'Snaps to stops · ± steps one tick',
  favoritesLabel: 'Favorites',
  morePresets: 'More presets',
  presetsSizeLabel: 'Presets · size',
  potSizeHintUnset: 'Set pot (BB) in setup to see pot size as bb',
  clearOptional: 'Clear',
  betRaiseHint: 'Bet: no prior aggression · Raise: facing a bet/raise',
  allinSizeHint: 'Optional all-in size',
  sizeActionHint: (potBit) =>
    potBit
      ? `Bet=first · Raise=vs bet · ${potBit}`
      : 'Bet=first · Raise=vs bet',
  addAction: (street) => `Add ${street} action`,
  updateAction: 'Update',
  editAction: 'Edit',
  cancelEdit: 'Cancel edit',
  noStreetActions: (street) => `No ${street} actions yet.`,
  delete: 'Delete',
  moveUp: 'Up',
  moveDown: 'Down',
  allActionsHint: (n) => `${n} actions total · check other street tabs`,
  heroChip: (pos) => pos,
  seatOrderHu: 'HU: BTN acts as SB · order BTN/SB → BB',
  seatOrder: (order) => `Seat order: ${order}`,
  positionHintHuBtn: 'HU button (SB)',
  keepActorHint: 'Street tabs keep the actor (only type/size reset)',
  potQuickPrompt: 'Quick pot (BB) — tap to set and refresh the hint',
  potQuickClear: 'Clear pot',
  quickLinesLabel: 'Quick lines',
  quickLinesHint: 'Common templates — tap to add actions',
  macroOpenThenCall: 'Open then call',
  macroFoldToHero: (seat) => `Fold to ${seat}`,
  macroCbet33: 'C-bet ⅓ pot',
  quickLinesSwitchStreet: 'Switch street for macros',
  macroOpenExists: 'An open (raise) is already logged',
  macroAlreadyLogged: 'Open and call are already logged',
  macroFoldToHeroNone: (seat) => `No early folds left (or ${seat} acts first)`,
  macroFoldToHeroBlocked: 'Early action already logged — add folds manually',
  boardCtaFlop: 'Add flop cards',
  boardCtaTurn: 'Add turn card',
  boardCtaRiver: 'Add river card',
  quickHand: 'Quick hand',
  skipToCards: 'Skip to cards',
  jumpToActions: 'Go to line',
  skipBoardPreflop: 'Skip board — preflop only',
  boardSlotsToggle: 'Add board cards',
  softStackAria: 'Hand so far',
  softStackLine: 'Line',
  softStackHoles: 'Holes',
  softStackBoard: 'Board',
  streetTabWithCount: (street, n) => `${street} · ${n}`,
  actionIndexLabel: (n, street) => `Action ${n} · ${street}`,
  copyLinkTitle: 'Reopens this hand in the app',
  suitStickyHint: 'Suit held · tap a suit to change',
  suitStickyChip: (s) => `Current suit ${s}`,
  suitMruLabel: 'Recent',

  summaryHint:
    'Screenshot the process card below, or copy/share the text',
  summaryHole: 'Hole cards',
  summaryBoard: 'Board',
  summaryActions: 'Actions',
  timelineProcess: 'Process',
  timelineBoard: (cards) => `Board ${cards}`,
  processMoreSummary: 'More process',
  linePeekSummary: 'Line so far',
  takeawayLabel: 'Takeaway',
  takeawayPlaceholder: 'Seat + line only — e.g. BTN raise, BB call, flop check',
  takeawayHint: 'One process line — seat + line only',
  takeawayNudge: 'Process note (optional)\u00a0· seat\u00a0· line',
  takeawayInLinkFact: 'The process note is in the link',
  shareSecondarySummary: 'Note · opponents (optional)',
  shareSecondaryInvite: 'Leave a process one-liner (optional)',
  shareMoreSummary: 'More share options',
  lineDraftSummary: 'Actor · size',
  explainModeLabel: 'Explain',
  explainModeAria: 'Explain mode — show teaching hints',
  reviewScopeLabel: 'Review through',
  reviewScopePreflop: 'Preflop',
  reviewScopeFlop: 'Flop',
  reviewScopeRiver: 'River',
  reviewScopeAria: 'How far to review — folds later streets without deleting',
  shareProcessHu: 'Heads-up',
  shareProcessMultiway: 'Multiway',
  shareReviewedLabel: 'Reviewed',
  summaryCopyLabel: 'Paste into chat — readable + Pro',
  summaryProLabel: 'Pro notation',
  emptyDash: '—',
  copyBoth: 'Share to chat',
  copyHuman: 'Readable',
  copyPro: 'Pro notation',
  copied: 'Copied ✓',
  copiedToast: 'Hand card summary copied',
  screenshotHint: 'Capture = clean card shot · Link = reopen hand',
  captureMode: 'Capture',
  captureExit: 'Exit capture',
  saveShareImage: 'Save image',
  shareImage: 'Share image',
  imageSaved: 'Saved ✓',
  imageShared: 'Shared ✓',
  imageSavedToast: 'Hand card image saved',
  imageSharedToast: 'Image shared',
  imageFailedToast: 'Could not create image',
  shareText: 'Share text',
  shared: 'Shared ✓',
  copyLink: 'Copy link',
  linkCopiedToast: 'Hand link copied',
  linkWithoutKnownHolesToast:
    'Link copied without villain hole cards (too long with them)',
  linkTooLongToast: 'Hand too large for a share link',
  sharedToast: 'Shared',
  shareFailedToast: 'Share failed',
  newHand: 'New hand',
  receivedQuietRerecordCta: 'Record my own process',
  draftRestoredHint: 'Draft restored — continue or start fresh?',
  draftContinue: 'Continue',
  actorFoldedHint: 'Already folded',
  summaryHeading: '[Holecard EX hand summary]',
  summaryPlayers: (n) => `Players: ${n}-max`,
  summaryBlinds: (b) => `Blinds: ${b}`,
  summaryHero: (p) => `Seat: ${p}`,
  summaryStack: (s) => `Stack: ${s}`,
  summaryEff: (e) => `Eff stack: ${e} BB`,
  summaryPot: (p) => `Pot: ${p} BB`,
  summaryHoleLine: (h) => `Hole: ${h}`,
  summaryHolesHeader: 'Holes:',
  summaryKnownHoleLine: (label, cards) => `· ${label}: ${cards}`,
  summaryBoardLine: (b) => `Board: ${b}`,
  summaryActionsHeader: '── Actions ──',
  summaryFooter: '— Made with Holecard EX',
  summaryProHeader: '── Pro notation ──',
  tableLine: (n, blinds) => `${n}-max · ${blinds}`,

  errorBlinds:
    'Invalid blinds. BB must be greater than 0; SB must be ≥ 0 and ≤ BB.',
  hintFlopPartial: 'Flop is usually 3 cards',
  hintNoActions: 'No actions needed — “Finish record” shares the record so far',
  missingFlopBoard: 'Flop actions exist but flop board cards are missing',
  missingTurnBoard: 'Turn actions exist but the turn card is missing',
  missingRiverBoard: 'River actions exist but the river card is missing',
  boardActionsMismatch: 'Actions on a street whose board cards are empty',
  boardNeedFlop: 'Flop board is empty — add cards for a clearer review',
  boardNeedFlopPartial: 'Finish entering the flop cards',
  boardNeedTurn: 'Turn card is empty — add it for a clearer review',
  boardNeedRiver: 'River card is empty — add it for a clearer review',

  softGateTitle: 'Some fields are still empty',
  softGateBody: 'Hole cards are still empty. Add holes to record the process, or continue anyway.',
  softGateFix: "Add hole cards",
  softGateForceConfirm: "Really share incomplete?",
  softGateMissingHoles: (seat) => `${seat} hole cards (2)`,
  softGateMissingActions: "At least one action",
  softGateBodyLead: "Still needed:",
  incompleteBadge: "Incomplete",
  softGateContinue: 'View summary anyway',
  softGateShare: 'Share anyway',
  softGateCopy: 'Copy anyway',
  softGateCancel: 'Go back',
  pickerDisabled: 'No slot selected',

  step: {
    setup: 'Table',
    position: 'Seat',
    cards: 'Hand',
    board: 'Board',
    actions: 'Line',
    summary: 'Share',
  },
  street: {
    preflop: 'Preflop',
    flop: 'Flop',
    turn: 'Turn',
    river: 'River',
  },
  actionType: {
    fold: 'Fold',
    check: 'Check',
    call: 'Call',
    bet: 'Bet',
    raise: 'Raise',
    allin: 'All-in',
  },
  suit: {
    s: 'Spades',
    h: 'Hearts',
    d: 'Diamonds',
    c: 'Clubs',
  },
  positionHintMap: {
    UTG: 'Under the gun',
    'UTG+1': 'UTG+1',
    MP: 'Middle position',
    'MP+1': 'MP+1',
    HJ: 'Hijack',
    CO: 'Cutoff',
    BTN: 'Button (dealer)',
    SB: 'Small blind',
    BB: 'Big blind',
  },

  progressAria: (current, total, label) =>
    `Step ${current} of ${total}: ${label}`,
  adArea: 'Ad slot',
  adTop: 'Top ad slot',
  adBottom: 'Bottom ad slot',
  adPlaceholderHint: 'AdSense ID unset — placeholder until deploy',
  // Softwise M10b: memory-order input
  endHere: "End here",
  finishRecord: "Finish record",
  reviewAnte: (v) => `ante ${v}`,
  reviewEffStack: (v) => `eff ${v}`,
  reviewBreakdownAria: 'By street',
  reviewPlayers: (n) => `${n} players`,
  // vocab-tags:start — screen-only poker vocabulary (review breakdown)
  reviewVocab: {
    trips: 'trips',
    paired: 'paired',
    monotone: 'monotone',
    twoTone: 'two-tone',
    rainbow: 'rainbow',
    limp: 'limp',
    open: 'open',
    threeBet: '3-bet',
    fourBet: '4-bet',
    fiveBet: '5-bet',
    squeeze: 'squeeze',
    coldCall: 'cold call',
    checkRaise: 'check-raise',
    cBet: 'c-bet',
    donkBet: 'donk bet',
    probeBet: 'probe bet',
  },
  shareSavedMark: 'Saved',
  // vocab-tags:end
  flowAskFlop: "Flop · 3 cards",
  flowAskTurn: "Turn · 1 card",
  flowAskRiver: "River · 1 card",
  flowWhoActs: "To act",
  flowSizeCustom: "Custom",
  flowSizeConfirm: "OK",
  flowSizeLabel: "Size (bb)",
  flowDone: "Line recorded",
  flowRewind: "Redo from here",
  flowRewindCancel: "Cancel",
  flowSeatUnrecorded: "not recorded",
  flowSeatNotInvolved: "not in hand",
  flowMemoryCut: "Don't remember further",
  lineStripAria: "Line recorded so far",
  // Soft G: optional ending record (L1.5) — seat-subject, press-only, Quiet
  endingSummary: 'Ending (optional)',
  endingShownLabel: 'Cards shown at showdown',
  endingPotLabel: 'Pot',
  endingCardsInput: 'Enter cards',
  endingMuck: 'Not shown (muck)',
  endingUnknown: 'Unknown',
  endingPotTo: (seat) => `${seat} took the pot`,
  endingSplit: 'Split',
  endingLineLabel: 'Ending',
  endingShownCards: (seat, cards) => `${seat} showed ${cards}`,
  endingShownMuck: (seat) => `${seat} not shown (muck)`,
  endingShownUnknown: (seat) => `${seat} cards unknown`,
  endingPotSplit: 'Pot split',
  endingPotUnknown: 'Pot unknown',
  linkWithoutEndingToast: 'Link was long, so the ending was not included',
  linkWithoutTakeawayToast: 'Link was long, so the process note was not included',
  endingShownAria: (seat) => `${seat} showdown cards`,
}

export default en
